const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * @typedef {object} LabAffinityRule
 * @property {string} LabField - the Lab column matched against (e.g. 'Name', 'ExternalLabID').
 * @property {string} Pattern - a regular expression with `{KeyName}` placeholders filled from the project keys.
 * @property {Array<string>} [Requires] - project key names that must resolve before the rule is evaluated.
 * @property {string} [Flags] - regular-expression flags (default 'i').
 */

/**
 * @typedef {object} LabRoutingConfiguration
 * @property {Record<string, Array<string>>} [ProjectKeys] - key name -> ordered `Project.CustomProperties` keys; first non-empty wins.
 * @property {Array<LabAffinityRule>} [Rules] - ordered; the order is the routing priority order.
 * @property {string} [ResolverService] - fable service hash of a customer-specific router replacing this one.
 * @property {boolean} [FormsPerTestLab] - false to keep every test's form on one document regardless of lab.
 */

/**
 * @typedef {object} SampleLabContext
 * @property {Array<number>} [AffinityRootIDs] - affinity-matched parent labs, in priority order.
 * @property {Set<number>|Array<number>} [ProjectRootLabIDs] - the project's own parent labs (ProjectLabJoin).
 */

/**
 * @typedef {object} TestLabContext
 * @property {Set<number>} [TestLabIDs] - labs named by `TestLabJoin` for this test.
 * @property {Set<number>} [TestLabDefaultIDs] - labs named by `LabTestDefault` for this test (back-off axis).
 * @property {Set<number>|Array<number>} [ParentLabIDs] - the sample's assigned parent labs.
 * @property {Set<number>|Array<number>} [ProjectRootLabIDs] - the project's own parent labs.
 * @property {Set<number>|null} [SpecSetFloor] - the family-expanded `LabTestSpecificationSetJoin` floor.
 * @property {Array<number>} [AffinityRootIDs] - affinity-matched parent labs, in priority order.
 */

/**
 * @typedef {object} LabSelection
 * @property {number} IDLab - the chosen lab, or 0 when no axis answered.
 * @property {string} Source - which axis settled it: 'TestLabJoin' | 'LabTestDefault' | 'Affinity' |
 *   'ParentFamily' | 'Ambiguous' | 'None' for tests; 'LabTestDefault' | 'Affinity' | 'ProjectLabJoin' |
 *   'None' for samples.
 */

/**
 * MaterialsLabRouter — decides which lab a sample ships to and which bench runs each of its tests.
 *
 * The lab model is two-level: a PARENT lab is the routing destination a sample is assigned to, and its
 * CHILD labs are the benches that actually run tests. `ProjectLabJoin` names parents; `TestLabJoin` and
 * `LabTestDefault` name children. The concierge therefore asks two different questions:
 *
 *   - {@link selectSampleLabID} picks the PARENT lab for the sample's `SampleLabJoin`.
 *   - {@link selectTestLabID} picks the LEAF lab for a test's `TestInstance` and its form.
 *
 * Per-test narrowing intersects the axes that carry data — the test's capable labs (`TestLabJoin`, resolved
 * to leaves), the families of the sample's parent labs, and the families of the project's labs — then keeps
 * only leaves and applies the specification-set floor. `LabTestDefault` is a BACK-OFF axis: it narrows where
 * it has coverage but is never allowed to empty the candidates, because it disagrees with `TestLabJoin` often
 * enough that intersecting it hard would strand a test with no bench.
 *
 * Which of several surviving benches wins is customer data, not code: `LabRouting.Rules` match project
 * metadata (`Project.CustomProperties`) against Lab columns to name the parent labs belonging to a project —
 * the district laboratory and the field unit, for a DOT — and rule order is priority order. Without a
 * `LabRouting` block the affinity tier is simply absent and selection falls through to the data axes.
 *
 * This mirrors the web `LabScopeResolver` the sample form uses, so a form the concierge generates offers the
 * same labs the engineer would have picked by hand.
 */
class MaterialsLabRouter extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The Fable instance
	 * @param {Record<string, any>} [pOptions] - (optional) Options for the router
	 * @param {string} [pServiceHash] - (optional) Hash to use for the service
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'MaterialsLabRouter';

		/** @type {Map<number, Record<string, any>>} the customer lab catalog, by IDLab */
		this._Labs = new Map();
		/** @type {Map<number, Set<number>>} IDLab -> its child IDLab set */
		this._ChildrenByParent = new Map();
		this._LabsLoaded = false;
		/** @type {Map<number, Array<number>>} IDProject -> affinity-matched parent labs, in rule order */
		this._AffinityByProject = new Map();
		/** @type {{ get: (pURI: string) => Promise<any>, getAll: (pURI: string) => Promise<Array<any>> }|null} */
		this._Readers = null;
	}

	/**
	 * Bind the router to a caller's API readers. The router owns no client of its own so it reads through
	 * whatever session the caller is already impersonating.
	 *
	 * @param {{ get: (pURI: string) => Promise<any>, getAll: (pURI: string) => Promise<Array<any>> }} pReaders
	 *
	 * @return {void}
	 */
	setDataReaders(pReaders)
	{
		this._Readers = pReaders;
	}

	// --- lab tree ------------------------------------------------------------------------------------

	/**
	 * The customer's lab catalog, loaded once per instance — the tree does not move mid-sync. Read in full
	 * rather than through the Lite projection, which emits only `ID*`/`GUID*` columns and so carries neither
	 * the names nor the codes the affinity rules match on.
	 *
	 * The sort clause is also what makes this a legal paged read: meadow-endpoints exposes no bare
	 * `s/:Begin/:Cap`, only `s/FilteredTo/:Filter/:Begin/:Cap`.
	 *
	 * @return {Promise<void>}
	 */
	async loadLabCatalog()
	{
		if (this._LabsLoaded || !this._Readers)
		{
			return;
		}
		const tmpRows = await this._Readers.getAll(`Labs/FilteredTo/FSF~Name~ASC~0`);
		this.setLabCatalog(tmpRows);
	}

	/**
	 * Seed the lab tree from records already in hand (or from a test fixture), instead of reading them.
	 *
	 * @param {Array<Record<string, any>>} pLabs
	 *
	 * @return {void}
	 */
	setLabCatalog(pLabs)
	{
		this._Labs = new Map();
		this._ChildrenByParent = new Map();
		for (const tmpRow of (pLabs || []))
		{
			const tmpIDLab = parseInt(tmpRow.IDLab, 10);
			if (tmpIDLab > 0)
			{
				this._Labs.set(tmpIDLab, tmpRow);
			}
		}
		for (const tmpLab of this._Labs.values())
		{
			const tmpIDLab = parseInt(tmpLab.IDLab, 10);
			const tmpParent = parseInt(tmpLab.IDLabParent, 10) || 0;
			if (!(tmpParent > 0) || tmpParent === tmpIDLab || !this._Labs.has(tmpParent))
			{
				continue;
			}
			let tmpChildren = this._ChildrenByParent.get(tmpParent);
			if (!tmpChildren)
			{
				tmpChildren = new Set();
				this._ChildrenByParent.set(tmpParent, tmpChildren);
			}
			tmpChildren.add(tmpIDLab);
		}
		this._LabsLoaded = true;
	}

	/**
	 * Add lab records the caller already holds without discarding what is loaded. The concierge fetches the
	 * project's labs on its own path anyway, so merging them guarantees the tree covers every lab a decision
	 * touches even when the catalog read returned nothing.
	 *
	 * @param {Array<Record<string, any>>} pLabs
	 *
	 * @return {void}
	 */
	mergeLabCatalog(pLabs)
	{
		let tmpAdded = false;
		for (const tmpRow of (pLabs || []))
		{
			const tmpIDLab = parseInt(tmpRow.IDLab, 10);
			if (tmpIDLab > 0 && !this._Labs.has(tmpIDLab))
			{
				this._Labs.set(tmpIDLab, tmpRow);
				tmpAdded = true;
			}
		}
		if (tmpAdded)
		{
			this.setLabCatalog(Array.from(this._Labs.values()));
		}
	}

	/**
	 * The lab record from the catalog, or null when it is not loaded.
	 *
	 * @param {number|string} pIDLab
	 *
	 * @return {Record<string, any>|null}
	 */
	getLab(pIDLab)
	{
		return this._Labs.get(parseInt(String(pIDLab), 10)) || null;
	}

	/**
	 * The parent of a lab, or the lab itself when it is already a root. A parent pointer referencing a lab
	 * outside the catalog (or the lab itself) is treated as "no parent" rather than followed.
	 *
	 * @param {number|string} pIDLab
	 *
	 * @return {number}
	 */
	getParentLabID(pIDLab)
	{
		const tmpIDLab = parseInt(String(pIDLab), 10);
		if (!(tmpIDLab > 0))
		{
			return 0;
		}
		const tmpLab = this._Labs.get(tmpIDLab);
		const tmpParent = tmpLab ? (parseInt(tmpLab.IDLabParent, 10) || 0) : 0;
		if (!(tmpParent > 0) || tmpParent === tmpIDLab || !this._Labs.has(tmpParent))
		{
			return tmpIDLab;
		}
		return tmpParent;
	}

	/**
	 * Whether a lab is a leaf — it has no children, so a test assigned to it needs no further routing. A lab
	 * absent from the catalog counts as a leaf so an unloaded tree never hides an option.
	 *
	 * @param {number|string} pIDLab
	 *
	 * @return {boolean}
	 */
	isLeafLab(pIDLab)
	{
		const tmpIDLab = parseInt(String(pIDLab), 10);
		if (!(tmpIDLab > 0))
		{
			return false;
		}
		const tmpChildren = this._ChildrenByParent.get(tmpIDLab);
		return !tmpChildren || tmpChildren.size < 1;
	}

	/**
	 * The child labs of a lab.
	 *
	 * @param {number|string} pIDLab
	 *
	 * @return {Set<number>}
	 */
	getChildLabIDs(pIDLab)
	{
		return new Set(this._ChildrenByParent.get(parseInt(String(pIDLab), 10)) || []);
	}

	/**
	 * Resolve a set of labs to the leaves a test can actually be assigned to: a leaf stays, a parent is
	 * replaced by its children. Falls back to the input when the resolution empties it (a catalog that has
	 * not loaded), so this never removes the only options a caller had.
	 *
	 * @param {Set<number>|Array<number>|null|undefined} pLabIDs
	 *
	 * @return {Set<number>|null}
	 */
	resolveToLeaves(pLabIDs)
	{
		const tmpInput = this._toSet(pLabIDs);
		if (tmpInput.size < 1)
		{
			return null;
		}
		const tmpLeaves = new Set();
		for (const tmpIDLab of tmpInput)
		{
			const tmpChildren = this._ChildrenByParent.get(tmpIDLab);
			if (tmpChildren && tmpChildren.size > 0)
			{
				for (const tmpChild of tmpChildren)
				{
					tmpLeaves.add(tmpChild);
				}
				continue;
			}
			tmpLeaves.add(tmpIDLab);
		}
		return (tmpLeaves.size > 0) ? tmpLeaves : tmpInput;
	}

	/**
	 * Every lab in the families rooted at the given labs — each root plus its children. The root itself is
	 * included so a childless parent lab stays selectable per-test.
	 *
	 * @param {Set<number>|Array<number>} pRootIDs
	 *
	 * @return {Set<number>}
	 */
	expandFamilies(pRootIDs)
	{
		const tmpFamily = new Set();
		for (const tmpRoot of this._toSet(pRootIDs))
		{
			tmpFamily.add(tmpRoot);
			const tmpChildren = this._ChildrenByParent.get(tmpRoot);
			if (tmpChildren)
			{
				for (const tmpChild of tmpChildren)
				{
					tmpFamily.add(tmpChild);
				}
			}
		}
		return tmpFamily;
	}

	/**
	 * The distinct parent labs standing over a set of labs.
	 *
	 * @param {Set<number>|Array<number>} pLabIDs
	 *
	 * @return {Set<number>}
	 */
	rootsOf(pLabIDs)
	{
		const tmpRoots = new Set();
		for (const tmpIDLab of this._toSet(pLabIDs))
		{
			const tmpRoot = this.getParentLabID(tmpIDLab);
			if (tmpRoot > 0)
			{
				tmpRoots.add(tmpRoot);
			}
		}
		return tmpRoots;
	}

	/**
	 * Collapse a bench to the lab its results aggregate to. `AggregateTo` on a PARENT means its benches'
	 * tests roll up to it; `AggregateTo` on the bench itself means the bench is the aggregation point and
	 * stays put.
	 *
	 * @param {number|string} pIDLab
	 *
	 * @return {number}
	 */
	applyAggregation(pIDLab)
	{
		const tmpIDLab = parseInt(String(pIDLab), 10);
		if (!(tmpIDLab > 0))
		{
			return 0;
		}
		const tmpLab = this._Labs.get(tmpIDLab);
		if (!tmpLab || tmpLab.AggregateTo)
		{
			return tmpIDLab;
		}
		const tmpParentID = this.getParentLabID(tmpIDLab);
		const tmpParent = (tmpParentID !== tmpIDLab) ? this._Labs.get(tmpParentID) : null;
		return (tmpParent && tmpParent.AggregateTo) ? tmpParentID : tmpIDLab;
	}

	/**
	 * The specification-set lab floor, expanded across the lab family: a lab is admitted when IT is in
	 * `LabTestSpecificationSetJoin`, when its PARENT is, or when any of its CHILDREN is.
	 *
	 * Membership is declared at the FAMILY level — the set names the parent labs and their benches come
	 * along — so raw membership empties whichever level the set was not authored at. The floor's real job,
	 * separating parallel lab generations mid-rename, survives the expansion because generations differ by
	 * FAMILY: expanding within a family never reaches across to the other one.
	 *
	 * @param {Set<number>|Array<number>|null} pAdmittedLabIDs - the raw joined lab ids.
	 *
	 * @return {Set<number>|null} null when the join has no coverage (no floor at all).
	 */
	expandSpecSetFloor(pAdmittedLabIDs)
	{
		const tmpAdmitted = this._toSet(pAdmittedLabIDs);
		if (tmpAdmitted.size < 1)
		{
			return null;
		}
		const tmpFloor = new Set(tmpAdmitted);
		for (const tmpIDLab of tmpAdmitted)
		{
			const tmpParent = this.getParentLabID(tmpIDLab);
			if (tmpParent > 0)
			{
				tmpFloor.add(tmpParent);
			}
			for (const tmpChild of this.getChildLabIDs(tmpIDLab))
			{
				tmpFloor.add(tmpChild);
			}
		}
		return tmpFloor;
	}

	// --- selection -----------------------------------------------------------------------------------

	/**
	 * The PARENT lab a sample should be assigned to.
	 *
	 * Candidates are the parents standing over whatever the caller resolved from `LabTestDefault`, ranked:
	 * an affinity-matched parent (in rule order — the project's own district laboratory before its field
	 * unit) wins, then one of the project's own labs, then the project's own affinity lab even though no
	 * candidate sits under it, and only then the first candidate, which is the answer this gave before any
	 * routing configuration existed.
	 *
	 * `LabTestDefault` is customer-wide: it names every lab that runs a material, in any district. When none
	 * of those labs belongs to the project, its first row says nothing about where THIS project's samples go,
	 * so the routing rules answer instead — the same district lab the web sample form pins first.
	 *
	 * With no candidates at all the affinity and project axes still answer on their own, so configuring
	 * routing rules ADDS defaulting where a material carried no `LabTestDefault` rather than removing it.
	 *
	 * @param {Set<number>|Array<number>} pCandidateLabIDs - labs resolved from `LabTestDefault` (any level).
	 * @param {SampleLabContext} [pContext]
	 *
	 * @return {LabSelection}
	 */
	selectSampleLabID(pCandidateLabIDs, pContext)
	{
		const tmpContext = pContext || {};
		const tmpAffinityRootIDs = Array.isArray(tmpContext.AffinityRootIDs) ? tmpContext.AffinityRootIDs : [];
		const tmpProjectRoots = this.rootsOf(tmpContext.ProjectRootLabIDs);
		const tmpCandidateRoots = this.rootsOf(pCandidateLabIDs);
		if (tmpCandidateRoots.size > 0)
		{
			for (const tmpAffinityRoot of tmpAffinityRootIDs)
			{
				if (tmpCandidateRoots.has(tmpAffinityRoot))
				{
					return { IDLab: tmpAffinityRoot, Source: 'Affinity' };
				}
			}
			const tmpInProject = this._lowest(this._intersect([ tmpCandidateRoots, tmpProjectRoots ]));
			if (tmpInProject > 0)
			{
				return { IDLab: tmpInProject, Source: 'ProjectLabJoin' };
			}
		}
		for (const tmpAffinityRoot of tmpAffinityRootIDs)
		{
			if (tmpProjectRoots.size < 1 || tmpProjectRoots.has(tmpAffinityRoot))
			{
				return { IDLab: tmpAffinityRoot, Source: 'Affinity' };
			}
		}
		if (tmpCandidateRoots.size > 0)
		{
			return { IDLab: Array.from(tmpCandidateRoots)[0], Source: 'LabTestDefault' };
		}
		return { IDLab: 0, Source: 'None' };
	}

	/**
	 * The LEAF lab that should run a test.
	 *
	 * Intersects the axes that carry data — the test's capable labs, the families of the sample's parent
	 * labs, and the families of the project's labs — keeps only leaves, applies the specification-set floor,
	 * then narrows by `LabTestDefault` where that does not empty the result. A tie is broken only when
	 * something distinguishes the benches — the project's own labs in routing-rule order, then the sample's
	 * lab families — and each tier must resolve to exactly one bench to settle it.
	 *
	 * Returns 0 when no axis carries data, the floor admits nothing, or the tie stands; the caller falls back
	 * to the sample's parent lab rather than guessing a bench.
	 *
	 * @param {number|string} pIDTest
	 * @param {TestLabContext} [pContext]
	 *
	 * @return {LabSelection}
	 */
	selectTestLabID(pIDTest, pContext)
	{
		const tmpContext = pContext || {};
		/** @type {Array<Set<number>>} */
		const tmpAxes = [];
		const tmpTestLabs = this.resolveToLeaves(tmpContext.TestLabIDs);
		if (tmpTestLabs)
		{
			tmpAxes.push(tmpTestLabs);
		}
		const tmpParentFamilies = this.expandFamilies(tmpContext.ParentLabIDs);
		if (tmpParentFamilies.size > 0)
		{
			tmpAxes.push(tmpParentFamilies);
		}
		const tmpProjectFamilies = this.expandFamilies(tmpContext.ProjectRootLabIDs);
		if (tmpProjectFamilies.size > 0)
		{
			tmpAxes.push(tmpProjectFamilies);
		}
		if (tmpAxes.length < 1)
		{
			return { IDLab: 0, Source: 'None' };
		}
		// Leaf grain is an invariant of this selection, not a property of one axis: the family axes carry
		// their roots so a childless parent stays selectable, and with no TestLabJoin rows those roots
		// would otherwise be chosen.
		let tmpCandidates = this._keepLeaves(this._intersect(tmpAxes));
		if (tmpContext.SpecSetFloor && tmpContext.SpecSetFloor.size > 0)
		{
			tmpCandidates = this._intersect([ tmpCandidates, tmpContext.SpecSetFloor ]);
		}
		if (tmpCandidates.size < 1)
		{
			return { IDLab: 0, Source: 'None' };
		}
		let tmpSource = tmpTestLabs ? 'TestLabJoin' : 'ParentFamily';
		const tmpDefaults = this.resolveToLeaves(tmpContext.TestLabDefaultIDs);
		if (tmpDefaults)
		{
			const tmpNarrowed = this._intersect([ tmpCandidates, tmpDefaults ]);
			if (tmpNarrowed.size > 0)
			{
				tmpCandidates = tmpNarrowed;
				tmpSource = 'LabTestDefault';
			}
		}
		if (tmpCandidates.size === 1)
		{
			return { IDLab: Array.from(tmpCandidates)[0], Source: tmpSource };
		}
		// Ties are broken only where something actually distinguishes the benches — the project's own labs in
		// routing-rule order, then the sample's lab families. Guessing between equals is how a router ends up
		// sending a test to the wrong bench, so an unbroken tie declines and the caller keeps the sample's
		// parent lab, where a technician can route it by hand.
		const tmpAffinityRootIDs = Array.isArray(tmpContext.AffinityRootIDs) ? tmpContext.AffinityRootIDs : [];
		for (const tmpAffinityRoot of tmpAffinityRootIDs)
		{
			const tmpInFamily = this._filterByRoot(tmpCandidates, tmpAffinityRoot);
			if (tmpInFamily.size === 1)
			{
				return { IDLab: Array.from(tmpInFamily)[0], Source: 'Affinity' };
			}
		}
		const tmpParentRoots = this.rootsOf(tmpContext.ParentLabIDs);
		const tmpInParentFamily = new Set();
		for (const tmpIDLab of tmpCandidates)
		{
			if (tmpParentRoots.has(this.getParentLabID(tmpIDLab)))
			{
				tmpInParentFamily.add(tmpIDLab);
			}
		}
		if (tmpInParentFamily.size === 1)
		{
			return { IDLab: Array.from(tmpInParentFamily)[0], Source: 'ParentFamily' };
		}
		return { IDLab: 0, Source: 'Ambiguous' };
	}

	// --- affinity ------------------------------------------------------------------------------------

	/**
	 * The parent labs belonging to a project, from the `LabRouting` naming rules, in rule (priority) order.
	 * Cached per project. Every step fails open to "no affinity", which costs the routing preference and
	 * nothing else — the data axes still answer.
	 *
	 * @param {number} pIDProject
	 * @param {LabRoutingConfiguration} [pLabRoutingConfig]
	 *
	 * @return {Promise<Array<number>>}
	 */
	async getAffinityRootIDs(pIDProject, pLabRoutingConfig)
	{
		if (!pLabRoutingConfig || !Array.isArray(pLabRoutingConfig.Rules) || pLabRoutingConfig.Rules.length < 1 || !(pIDProject > 0))
		{
			return [];
		}
		if (this._AffinityByProject.has(pIDProject))
		{
			return this._AffinityByProject.get(pIDProject);
		}
		let tmpMatched = [];
		try
		{
			await this.loadLabCatalog();
			const tmpProject = this._Readers ? await this._Readers.get(`Project/${pIDProject}`) : null;
			const tmpKeys = this.resolveAffinityKeys(pLabRoutingConfig, this.readCustomProperties(tmpProject));
			tmpMatched = this.matchAffinityRules(pLabRoutingConfig, tmpKeys);
			this.fable.log.info(`Lab routing affinity for project ${pIDProject} resolved keys ${JSON.stringify(tmpKeys)} to parent labs [${tmpMatched.join(',')}]`);
		}
		catch (pError)
		{
			this.fable.log.warn(`Lab routing affinity failed to resolve for project ${pIDProject}; continuing without it: ${pError.message}`);
			tmpMatched = [];
		}
		this._AffinityByProject.set(pIDProject, tmpMatched);
		return tmpMatched;
	}

	/**
	 * `CustomProperties` as an object, whether it arrives parsed or as a JSON string.
	 *
	 * @param {Record<string, any>|null} pProject
	 *
	 * @return {Record<string, any>}
	 */
	readCustomProperties(pProject)
	{
		const tmpRaw = pProject ? pProject.CustomProperties : null;
		if (!tmpRaw)
		{
			return {};
		}
		if (typeof tmpRaw !== 'string')
		{
			return tmpRaw;
		}
		try
		{
			return JSON.parse(tmpRaw) || {};
		}
		catch (pError)
		{
			return {};
		}
	}

	/**
	 * Resolve each configured key to its first non-empty project value. A value that is all digits and
	 * numerically zero is the customer's "unassigned" sentinel and counts as unset.
	 *
	 * @param {LabRoutingConfiguration} pLabRoutingConfig
	 * @param {Record<string, any>} pProperties
	 *
	 * @return {Record<string, string>}
	 */
	resolveAffinityKeys(pLabRoutingConfig, pProperties)
	{
		/** @type {Record<string, string>} */
		const tmpKeys = {};
		const tmpProjectKeys = pLabRoutingConfig.ProjectKeys || {};
		for (const tmpName of Object.keys(tmpProjectKeys))
		{
			const tmpCandidates = Array.isArray(tmpProjectKeys[tmpName]) ? tmpProjectKeys[tmpName] : [ tmpProjectKeys[tmpName] ];
			for (const tmpCandidate of tmpCandidates)
			{
				const tmpRaw = (pProperties || {})[tmpCandidate];
				const tmpValue = (tmpRaw === null || tmpRaw === undefined) ? '' : String(tmpRaw).trim();
				if (tmpValue === '' || (/^\d+$/.test(tmpValue) && Number(tmpValue) === 0))
				{
					continue;
				}
				tmpKeys[tmpName] = tmpValue;
				break;
			}
		}
		return tmpKeys;
	}

	/**
	 * The parent labs matched by the routing rules, in rule order.
	 *
	 * @param {LabRoutingConfiguration} pLabRoutingConfig
	 * @param {Record<string, string>} pKeys
	 *
	 * @return {Array<number>}
	 */
	matchAffinityRules(pLabRoutingConfig, pKeys)
	{
		const tmpMatched = [];
		for (const tmpRule of (pLabRoutingConfig.Rules || []))
		{
			if (!tmpRule || !tmpRule.LabField || !tmpRule.Pattern)
			{
				continue;
			}
			const tmpRequires = Array.isArray(tmpRule.Requires) ? tmpRule.Requires : Object.keys(pKeys);
			if (tmpRequires.some((pRequired) => !pKeys[pRequired]))
			{
				continue;
			}
			let tmpExpression;
			try
			{
				tmpExpression = new RegExp(this.interpolatePattern(tmpRule.Pattern, pKeys), tmpRule.Flags || 'i');
			}
			catch (pError)
			{
				this.fable.log.warn(`Lab routing rule pattern is not a valid expression: ${tmpRule.Pattern}`);
				continue;
			}
			for (const tmpLab of this._Labs.values())
			{
				const tmpIDLab = parseInt(tmpLab.IDLab, 10);
				// Routing rules name the destination, so only parents are matched; benches inherit.
				if (this.getParentLabID(tmpIDLab) !== tmpIDLab || tmpMatched.indexOf(tmpIDLab) > -1)
				{
					continue;
				}
				const tmpFieldValue = (tmpLab[tmpRule.LabField] === null || tmpLab[tmpRule.LabField] === undefined) ? '' : tmpLab[tmpRule.LabField];
				if (tmpExpression.test(String(tmpFieldValue)))
				{
					tmpMatched.push(tmpIDLab);
				}
			}
		}
		return tmpMatched;
	}

	/**
	 * Fill `{KeyName}` placeholders in a rule pattern. An all-digits value interpolates as `0*<digits>` with
	 * leading zeros stripped, so one pattern matches both `District 02 Lab` and `District 2 Lab` and the
	 * customer's project metadata does not have to agree with its lab names on zero-padding. Anything else is
	 * escaped and matched literally.
	 *
	 * @param {string} pPattern
	 * @param {Record<string, string>} pKeys
	 *
	 * @return {string}
	 */
	interpolatePattern(pPattern, pKeys)
	{
		return String(pPattern).replace(/\{([A-Za-z0-9_]+)\}/g, (pMatch, pName) =>
		{
			const tmpValue = pKeys[pName];
			if (tmpValue === undefined)
			{
				return pMatch;
			}
			if (/^\d+$/.test(tmpValue))
			{
				return `0*${String(Number(tmpValue))}`;
			}
			return tmpValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		});
	}

	// --- helpers -------------------------------------------------------------------------------------

	/**
	 * @param {Set<number>|Array<number>|null|undefined} pValues
	 *
	 * @return {Set<number>} the positive integers in the input.
	 */
	_toSet(pValues)
	{
		const tmpSet = new Set();
		for (const tmpValue of (pValues || []))
		{
			const tmpID = parseInt(String(tmpValue), 10);
			if (tmpID > 0)
			{
				tmpSet.add(tmpID);
			}
		}
		return tmpSet;
	}

	/**
	 * @param {Array<Set<number>>} pSets
	 *
	 * @return {Set<number>}
	 */
	_intersect(pSets)
	{
		const tmpResult = new Set(pSets[0]);
		for (let i = 1; i < pSets.length; i++)
		{
			for (const tmpValue of Array.from(tmpResult))
			{
				if (!pSets[i].has(tmpValue))
				{
					tmpResult.delete(tmpValue);
				}
			}
		}
		return tmpResult;
	}

	/**
	 * Drop any lab that routes further — a parent with benches beneath it. Its benches, when they qualify,
	 * are already in the set beside it.
	 *
	 * @param {Set<number>} pLabIDs
	 *
	 * @return {Set<number>}
	 */
	_keepLeaves(pLabIDs)
	{
		const tmpLeaves = new Set();
		for (const tmpIDLab of pLabIDs)
		{
			if (this.isLeafLab(tmpIDLab))
			{
				tmpLeaves.add(tmpIDLab);
			}
		}
		return tmpLeaves;
	}

	/**
	 * @param {Set<number>} pLabIDs
	 * @param {number} pRootID
	 *
	 * @return {Set<number>} the members of pLabIDs standing under pRootID.
	 */
	_filterByRoot(pLabIDs, pRootID)
	{
		const tmpMatched = new Set();
		for (const tmpIDLab of pLabIDs)
		{
			if (this.getParentLabID(tmpIDLab) === pRootID)
			{
				tmpMatched.add(tmpIDLab);
			}
		}
		return tmpMatched;
	}

	/**
	 * @param {Set<number>} pLabIDs
	 *
	 * @return {number} the lowest id in the set, or 0 when it is empty.
	 */
	_lowest(pLabIDs)
	{
		let tmpLowest = 0;
		for (const tmpIDLab of pLabIDs)
		{
			if (tmpLowest === 0 || tmpIDLab < tmpLowest)
			{
				tmpLowest = tmpIDLab;
			}
		}
		return tmpLowest;
	}
}

module.exports = MaterialsLabRouter;
